import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { debounce } from 'lodash'
import { type PageLayoutStore } from 'shared/layout'
import { type ICardFilter, TableStore } from 'shared/ui'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import { HubspotWorkflow } from 'entities/HubspotWorkflows/model/HubspotWorkflow'
import {
  type IParamsGetContactsMessages,
  type IResponseGetHubspotContactsStatistic,
  type IResponseHubspotWorkflow,
} from 'entities/HubspotWorkflows/api/types'
import {
  ContactStatistic,
  type IResponseContactStatistic,
  type IContactsStatisticFilter,
} from 'entities/ContactStatistic'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { HubspotWorkflowsApi } from 'entities/HubspotWorkflows/api/hubspotWorkflows'
import { type IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'

export type IContactStatistic = Partial<ContactStatistic> & {
  id: number
  body: string
  type: string
  sent_at: string | null
  number_id: number
}

export class HubspotWorkflowViewStore {
  private _id: string | undefined
  private _pageLayoutStore: PageLayoutStore
  private _contactsDetailsManageStore: ContactsDetailsManageStore
  private _workflow: HubspotWorkflow | null = null
  private _filter: IContactsStatisticFilter = 'sent_to'
  private _search = ''
  private _page = 1
  private _limit = 10
  private _total = 0
  private _calendarFilterStore = new CalendarFilterStore()
  private _contactStatisticsMap: Map<number, IContactStatistic> = new Map()
  private _contactStatisticsTableStore = new TableStore<IContactStatistic>({
    element: 'contact',
    withoutDefaultManageColumns: true,
  })
  private _disposeFetchContacts: IReactionDisposer | null = null
  private _disposeChangeParams: IReactionDisposer | null = null
  private _previousSearchValue = ''

  constructor(pageLayoutStore: PageLayoutStore, private _entities: IEntitiesStore, id?: string) {
    this._id = id
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._pageLayoutStore = pageLayoutStore

    makeAutoObservable(this)
  }

  init = () => {
    this._contactsDetailsManageStore.init()
    this.fetchContacts()

    this.reactionFetchContacts()
    this.reactionChangeParams()

    return this.dispose
  }

  reactionChangeParams = () => {
    this._disposeChangeParams?.()
    this._disposeChangeParams = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._requestLoadContacts.setLoading(true)
        this._page = 1
      }
    )
  }

  reactionFetchContacts = () => {
    this._disposeFetchContacts?.()
    this._disposeFetchContacts = reaction(
      () => this.params,
      () => this._requestLoadContacts.debounceLoadData()
    )
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get workflow() {
    return this._workflow
  }

  get filter() {
    return this._filter
  }

  get search() {
    return this._search
  }

  get statistics() {
    return this._workflow?.statistics
  }

  get page() {
    return this._page
  }

  get limit() {
    return this._limit
  }

  get total() {
    return this._total
  }

  get params() {
    return {
      page: this._page,
      ...this.paramsWithoutPage,
    }
  }

  get paramsWithoutPage(): IParamsGetContactsMessages {
    return {
      search: this._search,
      length: this._limit,
      filter: this._filter,
      dateFrom: this.formatDateToUTC(this.calendarFilterStore.sendDate?.from),
      dateTo: this.formatDateToUTC(this.calendarFilterStore.sendDate?.to),
    }
  }

  get isContactLoading() {
    return this._requestLoadContacts.loading
  }

  get isNoContactData() {
    return !this.isContactLoading && !this._contactStatisticsMap.size && !this.search
  }

  get contactStatisticsItems() {
    return Array.from(this._contactStatisticsMap.values())
  }

  get filters() {
    const filters = this.statistics?.filters
    const relevantColumnsIds = [
      'sent_to',
      'quiet_hours',
      'success',
      'replied',
      'no_replies',
      'clicked',
      'failed',
      'opted_out',
    ]

    return filters?.filter((column) =>
      relevantColumnsIds.includes(column.key)
    ) as ICardFilter<IContactsStatisticFilter>[]
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get contactStatisticsTableStore() {
    return this._contactStatisticsTableStore
  }

  getInboxId = (inboxId?: number, numberId?: number) => {
    if (inboxId) {
      const inbox = this._entities.inboxesStore.getItem(inboxId)

      return inbox?.id
    }

    if (numberId) {
      const inbox = this._entities.inboxesStore.getInboxByNumber(numberId)

      return inbox?.id
    }
    return undefined
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
            inboxId: this.getInboxId(data.inboxId, data.numberId),
          },
          'hubspotWorkflows',
          true
        )
      },
    }
  }

  private formatDateToUTC = (date: Date | null | undefined) => {
    return date ? uiStore.dayjs(date).utc().format(DayjsFormats.apiFormat2) : null
  }

  private _debouncedTrackSearchActivity = debounce((searchQuery: string) => {
    this._entities.organizationStore.handleTrackOrganizationActivity({
      event: 'phi.contact.searched',
      data: {
        search_query: searchQuery,
      },
    })
  }, 2000)

  private _requestLoadContacts = new RequestLoadData({
    requestCallback: async () => {
      const { data } = await HubspotWorkflowsApi.getHubspotWorkflowContactsMessages(
        this._id as string,
        this.params
      )
      if (
        this.search !== this._previousSearchValue &&
        this._entities.organizationStore.isHipaaCompliant
      ) {
        this._previousSearchValue = this.search
        this._debouncedTrackSearchActivity(this.search)
      }

      this.setContactsData(data)
    },
  })

  fetchContacts = async () => {
    return this._requestLoadContacts.loadData()
  }

  setWorkflow = (workflowData: IResponseHubspotWorkflow) => {
    this._workflow = new HubspotWorkflow(workflowData, this._entities)
    this.calendarFilterStore.updateCreatedAt(this._workflow?.firstMessageDate)
    this.calendarFilterStore.setFromFilterDefaultDate()
  }

  setContactsData = ({ data, meta }: IResponseGetHubspotContactsStatistic) => {
    this._contactStatisticsMap.clear()
    this._total = meta.total
    this._page = meta.current_page

    data.forEach((contactStatistics) => {
      const { conversation, id, contact, body, type, sent_at } = contactStatistics
      const contactStatistic = new ContactStatistic(contact as IResponseContactStatistic)
      this._contactStatisticsMap.set(
        id,
        Object.assign(contactStatistic, {
          body,
          type,
          sent_at,
          team_id: conversation?.team_id || undefined,
          number_id: conversation.number_id,
        })
      )
    })
  }

  setSearch = (value: string) => {
    this._search = value
  }

  onActiveFilter = (filter: ICardFilter<IContactsStatisticFilter>) => {
    this._filter = filter.key

    this.resetSearchAndFilters()
  }

  resetSearchAndFilters = () => {
    this.setSearch('')
    this.calendarFilterStore.resetSearchAndFilters()
  }

  onPaginationTableChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  dispose = () => {
    this._disposeFetchContacts?.()
    this._contactsDetailsManageStore.reset()
    this._disposeChangeParams?.()
    this._requestLoadContacts.dispose()
  }
}
